import AddressAutocomplete from '@orn/shared-web/components/AddressAutocomplete';
import Checkbox from '@orn/shared-web/components/Checkbox/Checkbox';
import DateOfBirthInput from '@orn/shared-web/components/DateOfBirthInput';
import Input, { PhoneNumberInput } from '@orn/shared-web/components/Input';
import Select from '@orn/shared-web/components/Select/Select';
import { MailIcon, PhoneHandsetIcon } from '@orn/shared-web/icons';

import { RELATIONSHIP_OPTIONS } from '../../../lib/dependants/constants';
import { displayName } from '../../../lib/dependants/helpers';
import { toResidenceAddress } from '../../../lib/dependants/residenceAddress';
import { CardSection } from '../common';

import { mergeFrequentPlaces, visibleFrequentPlaces } from './dependentCardModel';
import FrequentLocationsForm from './FrequentLocationsForm';
import ResidenceTypeFields from './ResidenceTypeFields';

import type { IDependantCardState, IResidenceFieldErrors, ResidenceMode } from './dependentCardModel';
import type { IFrequentLocationsState, IFrequentPlace } from './residenceForm';
import type { CaregiverRelationshipKind } from '../../../__generated__/globalTypes';
import type { IResidence } from '../../../hooks/queries/useDependantResidences';

/** Identity/contact errors plus the linked living-situation unit's (`IResidenceFieldErrors`). */
export interface IDependantCardErrors extends IResidenceFieldErrors {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  email?: string;
}

export interface IDependantCardBodyProps {
  dependantId: string;
  state: IDependantCardState;
  onChange: (patch: Partial<IDependantCardState>) => void;
  /** Persist pending edits — fired on text-input blur and on select/checkbox/pick changes. */
  onCommit: () => void;
  /** Main shows the full living-situation editor; Referential shows the "lives with {partnerName}" copy. */
  mode: ResidenceMode;
  /** True for the second card of a partnered pair — renders the "Lives with {partnerName}" checkbox. */
  isPartnerCard: boolean;
  partnerName: string | null;
  /** The partnership "live together" value + setter (only meaningful on the partner card). */
  liveTogether: boolean | null;
  onLiveTogetherChange: (value: boolean) => void;
  /** Shared, household-wide frequent-locations list (mirrored across every Main card). */
  frequentLocations: IFrequentLocationsState;
  onFrequentLocationsChange: (places: IFrequentPlace[]) => void;
  /** The caregiver's own primary residence, shown read-only when "lives with you" is checked. */
  caregiverResidence: IResidence | null;
  apiKey: string;
  /** Locks every input — only while the household state is being rebuilt after a residence create/delete. */
  isDisabled: boolean;
  errors: IDependantCardErrors;
}

/** Read-only display of the caregiver's address, shown when a dependant lives with the caregiver. */
const CaregiverResidenceDisplay = ({ residence }: { residence: IResidence | null }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-secondary text-sm font-medium">Address</span>
    <p className="text-tertiary text-sm">
      {residence ? (residence.formatted ?? residence.addressLine1 ?? residence.city) : 'Same as your address.'}
    </p>
  </div>
);
CaregiverResidenceDisplay.displayName = 'CaregiverResidenceDisplay';

/**
 * The editable sections for one dependent — Identity, Contact, Living
 * situation, and (Main mode) the household-shared Frequent locations. Card
 * chrome is supplied by the caller: the desktop accordion card or a
 * mobile/tablet person-tab panel. Edits persist via `onCommit` — text inputs
 * on blur, selects/checkboxes/picks on change; there is no save button. Fully
 * controlled — all state + callbacks come from the form wrapper.
 */
const DependantCardBody = ({
  dependantId,
  state,
  onChange,
  onCommit,
  mode,
  isPartnerCard,
  partnerName,
  liveTogether,
  onLiveTogetherChange,
  frequentLocations,
  onFrequentLocationsChange,
  caregiverResidence,
  apiKey,
  isDisabled,
  errors,
}: IDependantCardBodyProps) => {
  const name = displayName({ firstName: state.firstName, preferredName: state.preferredName }) || 'Loved one';
  const updateResidence = (patch: Partial<IDependantCardState['residence']>) =>
    onChange({ residence: { ...state.residence, ...patch } });
  // Saved rows mirror across cards (one household list); unsaved drafts render
  // only on the card that added them, joining the mirror once a save persists them.
  const cardFrequentPlaces = visibleFrequentPlaces(frequentLocations.places, dependantId);
  const handleFrequentPlacesChange = (nextVisible: IFrequentPlace[]) =>
    onFrequentLocationsChange(mergeFrequentPlaces(frequentLocations.places, dependantId, nextVisible));

  return (
    <>
      <CardSection label="Identity">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            value={state.firstName}
            onChange={(v) => onChange({ firstName: v })}
            onBlur={onCommit}
            size="md"
            isDisabled={isDisabled}
            isInvalid={Boolean(errors.firstName)}
            hint={errors.firstName}
          />
          <Input
            label="Last name"
            value={state.lastName}
            onChange={(v) => onChange({ lastName: v })}
            onBlur={onCommit}
            size="md"
            isDisabled={isDisabled}
            isInvalid={Boolean(errors.lastName)}
            hint={errors.lastName}
          />
        </div>
        <Input
          label="Preferred name"
          value={state.preferredName}
          onChange={(v) => onChange({ preferredName: v })}
          onBlur={onCommit}
          size="md"
          isDisabled={isDisabled}
          hint="We'll address them by this name in the app and emails"
        />

        <div className="flex flex-col gap-2">
          <Select
            label="Relationship to you"
            placeholder="Select a relationship"
            size="md"
            items={RELATIONSHIP_OPTIONS}
            selectedKey={state.relationship}
            onSelectionChange={(key) => {
              onChange({ relationship: key as CaregiverRelationshipKind });
              onCommit();
            }}
            isDisabled={isDisabled}
          >
            {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
          <div className="flex gap-6">
            <Checkbox
              isSelected={state.isStep}
              onChange={(v) => {
                onChange({ isStep: v });
                onCommit();
              }}
              size="md"
              label="Step"
            />
            <Checkbox
              isSelected={state.isInLaw}
              onChange={(v) => {
                onChange({ isInLaw: v });
                onCommit();
              }}
              size="md"
              label="In law"
            />
          </div>
        </div>

        <DateOfBirthInput
          value={state.dateOfBirth}
          onChange={(v) => {
            onChange({ dateOfBirth: v });
            onCommit();
          }}
          isInvalid={Boolean(errors.dateOfBirth)}
          hint={errors.dateOfBirth}
        />
      </CardSection>

      <div className="border-secondary border-t" />

      <CardSection label="Contact information">
        <Input
          label="Email address"
          icon={MailIcon}
          type="email"
          placeholder="name@email.com"
          value={state.email}
          onChange={(v) => onChange({ email: v })}
          onBlur={onCommit}
          size="md"
          isDisabled={isDisabled}
          isInvalid={Boolean(errors.email)}
          hint={errors.email}
        />
        <PhoneNumberInput
          label="Phone"
          icon={PhoneHandsetIcon}
          placeholder="(555) 000-0000"
          value={state.phone}
          onChange={(v) => onChange({ phone: v })}
          onBlur={onCommit}
          isDisabled={isDisabled}
        />
      </CardSection>

      <div className="border-secondary border-t" />

      <CardSection label="Living situation">
        {isPartnerCard && partnerName && (
          <Checkbox
            isSelected={liveTogether === true}
            onChange={onLiveTogetherChange}
            size="md"
            label={`Lives with ${partnerName}`}
            isDisabled={isDisabled}
          />
        )}

        {mode === 'referential' ? (
          <p className="text-tertiary text-sm">
            {name} lives with {partnerName}. Their residence details are on {partnerName}&rsquo;s card.
          </p>
        ) : (
          <>
            <Checkbox
              isSelected={state.residence.liveWithCaregiver === true}
              onChange={(v) => {
                updateResidence({ liveWithCaregiver: v });
                onCommit();
              }}
              size="md"
              label="Lives with you"
              isDisabled={isDisabled}
            />

            {state.residence.liveWithCaregiver === true ? (
              <CaregiverResidenceDisplay residence={caregiverResidence} />
            ) : (
              <>
                <AddressAutocomplete
                  apiKey={apiKey}
                  label="Address"
                  placeholder="123 Main St, San Francisco, CA"
                  value={state.residence.addressText}
                  includedPrimaryTypes={['street_address']}
                  isDisabled={isDisabled}
                  isInvalid={Boolean(errors.address)}
                  hint={errors.address}
                  onInputChange={(text) =>
                    updateResidence({
                      addressText: text,
                      address: state.residence.address?.formatted === text ? state.residence.address : null,
                    })
                  }
                  onSelect={(parsed) => {
                    updateResidence({ addressText: parsed.formattedAddress, address: toResidenceAddress(parsed) });
                    onCommit();
                  }}
                />
                <ResidenceTypeFields
                  state={state.residence}
                  onChange={updateResidence}
                  onCommit={onCommit}
                  errors={errors}
                  isDisabled={isDisabled}
                />
              </>
            )}
          </>
        )}
      </CardSection>

      {mode === 'main' && (
        <>
          <div className="border-secondary border-t" />

          <CardSection label="Frequent locations">
            <p className="text-secondary text-md">
              Is there anywhere away from home that {name} frequently spends time? We don&rsquo;t need exact locations,
              so you can enter cities or specific addresses.
            </p>
            <FrequentLocationsForm
              places={cardFrequentPlaces}
              onChange={handleFrequentPlacesChange}
              onCommit={onCommit}
              apiKey={apiKey}
              isDisabled={isDisabled}
              draftKey={dependantId}
            />
          </CardSection>
        </>
      )}
    </>
  );
};

DependantCardBody.displayName = 'DependantCardBody';

export default DependantCardBody;
